import { t } from '@lingui/macro'
import {
    DistanceCalculationMethod,
    EmissionFactorWithGasEmissions,
    GasEmissions,
} from '@lune-climate/lune'
import { Text } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import { FC, useMemo } from 'react'

import { useLuneI18nLocale } from '../../hooks'
import { getFormattedAmount } from '../../utils/utils'

import { DataQualityScoreSection } from './DataQualityScoreSection'
import EmissionFactorExplanationCard from './EmissionFactorExplanationCard'
import EmissionsBreakdowns from './EmissionsBreakdowns'
import EmissionsRouteExplanationWithMap from './EmissionsRouteExplanationWithMap'
import ExplanationSteps from './ExplanationSteps'
import {
    EmissionsExplanationSectionProps,
    getDistanceExplanation,
    useExplanationSteps,
} from './useExplanationSteps'
import { calculateEmissionFactorAmount, isAirShippingMethod } from './utils'

export const EmissionsExplanationSection: FC<EmissionsExplanationSectionProps> = (props) => {
    useLuneI18nLocale()

    const {
        distance,
        adjustedDistance,
        distanceCalculationMethod,
        load,
        convertedLoad,
        emissionFactor,
        emissions,
        emissionsUnit,
        responseRoute,
        requestRoute,
        requestMethod,
        mapboxAccessToken,
        hideEmissionFactorUrl,
        distanceCalculationDetails,
        vesselInference,
        pdfStructure,
        dataQualityScore,
        legIndex,
    } = props

    const steps = useExplanationSteps(props)
    const isSeaDistanceAlgorithm =
        distanceCalculationMethod === DistanceCalculationMethod.SEA_DISTANCE_ALGORITHM
    const isInlandWaterwayDistanceAlgorithm =
        distanceCalculationMethod === DistanceCalculationMethod.INLAND_DISTANCE_ALGORITHM
    const showMap =
        (distanceCalculationMethod === 'vessel_tracking' ||
            isSeaDistanceAlgorithm ||
            isInlandWaterwayDistanceAlgorithm ||
            isAirShippingMethod(requestMethod) ||
            distanceCalculationMethod === DistanceCalculationMethod.GRAPHHOPPER) &&
        responseRoute

    const localEmissionFactorAmount = calculateEmissionFactorAmount(
        convertedLoad,
        emissionFactor,
        load,
        adjustedDistance,
        emissions,
    )
    const wtwUnit = emissionsUnit ?? getFormattedAmount(emissions.amount).amountUnit

    const distanceExplanation = useMemo(() => {
        return getDistanceExplanation(
            distance,
            adjustedDistance,
            distanceCalculationMethod,
            distanceCalculationDetails,
            responseRoute,
            vesselInference,
            requestRoute,
        )
    }, [
        distance,
        adjustedDistance,
        distanceCalculationMethod,
        distanceCalculationDetails,
        responseRoute,
        vesselInference,
        requestRoute,
    ])

    return (
        <>
            <Box>
                <Text variant={'h5'} sx={{ mb: 4 }}>
                    {t`Methodology`}
                </Text>
                <ExplanationSteps steps={steps} pdfStructure={pdfStructure} />
            </Box>
            {dataQualityScore && (
                <DataQualityScoreSection
                    pdfStructure={pdfStructure}
                    dataQualityScore={dataQualityScore}
                    legIndex={legIndex?.toString()}
                />
            )}
            <EmissionsBreakdowns
                emissions={emissions}
                emissionsUnit={wtwUnit}
                pdfStructure={pdfStructure}
            />
            <EmissionFactorExplanationCard
                {...(emissionFactor.gasEmissions === null
                    ? {
                          emissionFactor: emissionFactor as Omit<
                              EmissionFactorWithGasEmissions,
                              'gasEmissions'
                          > & { gasEmissions: null },
                          amount: localEmissionFactorAmount.toString(),
                      }
                    : {
                          emissionFactor: emissionFactor as Omit<
                              EmissionFactorWithGasEmissions,
                              'gasEmissions'
                          > & { gasEmissions: GasEmissions },
                          amount: null,
                      })}
                label={emissionFactor.name}
                href={hideEmissionFactorUrl ? undefined : `/emission-factors/${emissionFactor.id}`}
            />
            {showMap && mapboxAccessToken && (
                <EmissionsRouteExplanationWithMap
                    mapboxAccessToken={mapboxAccessToken}
                    pdfStructure={pdfStructure}
                    distanceExplanation={distanceExplanation}
                    responseRoute={responseRoute}
                    requestRoute={requestRoute}
                    requestMethod={requestMethod}
                    distance={distance}
                    routeType={isSeaDistanceAlgorithm ? 'representative' : 'real'}
                    routeInferenceLegExplanation={props.routeInferenceLegExplanation}
                />
            )}
        </>
    )
}

export default EmissionsExplanationSection
